/**
 * *********************************************************************************************************************
 * Responsibility - First modal during the approval process if the user is approving/ rejecting all. This modal makes
 * the user acknowledge that they are about to perform an action on all the designs. 🐺
 * ************************************************************************************
 *
 * @format
 */

import {SelectWithErrorHandler, TextAreaWithErrorHandler} from 'frontier-form';
import {DESIGN_DISAPPROVE_REASON_NEW_ARTWORK} from 'frontier-constants';
import {ModalHeader, ModalFooter, Modal} from '@sttark/design-system';
import {useProofingContext} from '../proofing_context_provider';
import React, {useState, useEffect} from 'react';
import {IfElse, If} from 'frontier-utilities';

// styles
import './modals.scss';

export const RejectAll = props => {
    const proofingCtx = useProofingContext();
    const {children} = props;

    const {designsOrders, currentProof, isSubmitting, complete, formData, error} = proofingCtx.data;
    const {handleDesignDisapproveReason, handleDesignDisapproveReasonId, handleRejectAll} =
        proofingCtx;

    const [minHeight, setMinHeight] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const childWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                onClick: () => setIsOpen(true)
            });
        }
        return child;
    });

    const handleClose = () => {
        setIsOpen(false);
        setMinHeight('');
    };

    const handleExpandModal = () => {
        if (minHeight === '') setMinHeight('300px');
        else setMinHeight('');
    };

    const handleReasonSelection = value => {
        handleDesignDisapproveReasonId({target: {value}});
    };

    const handleTriggerReject = () => {
        handleRejectAll(currentProof.id);
    };

    const canRejectProof = formData.disapproveReason.length > 4 && !!formData.reasonId;
    const shouldShowAlert =
        [3, 4].indexOf(parseInt(formData.reasonId)) > -1 && parseInt(formData.reasonId) === 2;

    useEffect(() => {
        if (complete > 0 && !isSubmitting) {
            setIsOpen(false);
        }
    }, [complete, isSubmitting]);

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} bodyHeightAuto>
                <ModalHeader>Reject Artwork</ModalHeader>
                <div className="confirm-reject-approve-all-79lm__warning d-flex align-items-center justify-content-start gap-3">
                    <i className="fa fa-warning" />
                    <p className="approve-modal-23ll__p mt-0 mb-4">
                        <b>Warning!</b> You are about to reject the entire proof which includes{' '}
                        {designsOrders.length} pieces of artwork.
                    </p>
                </div>

                <div className="px-0 py-3 disapprove-all-11kt__modal-body">
                    <p className="disapprove-modal-23ll__p">
                        Select a reason for rejection and enter any additional details.
                    </p>
                    <div
                        className="disapprove-all-11kt__modal-select"
                        onClick={handleExpandModal}
                        style={{minHeight}}>
                        <SelectWithErrorHandler
                            className="form-control form-control-sm mb-4"
                            placeholder="Select reason for rejection"
                            onChange={handleReasonSelection}
                            errorPath="disapproveReason"
                            value={formData.reasonId}
                            label="Select a reason"
                            options={[
                                {id: 2, label: 'Replace or Add Artwork'},
                                {id: 3, label: 'Request Artwork Edit'},
                                {id: 4, label: 'Change Order (size, quantities, shipping, ect.)'},
                                {id: 5, label: 'I Have A Question'}
                            ]}
                            error={error}
                            newestds
                        />
                    </div>
                    <If condition={formData.reasonId !== DESIGN_DISAPPROVE_REASON_NEW_ARTWORK}>
                        <TextAreaWithErrorHandler
                            onChange={val => {
                                handleDesignDisapproveReason(val);
                            }}
                            className="disapprove-all-11kt__modal-text-area"
                            placeholder="Please tell us why..."
                            value={formData.disapproveReason}
                            errorPath="disapproveReason"
                            error={error}
                        />
                    </If>
                    <If condtion={shouldShowAlert}>
                        <div className="alert alert-info">
                            <If condition={[3, 4].indexOf(parseInt(formData.reasonId)) > -1}>
                                <p>
                                    Changes or requests might affect the cost. We will issues
                                    refunds or charges accordingly.
                                </p>
                            </If>
                            <If condition={parseInt(formData.reasonId) === 2}>
                                <p>
                                    Your order will go into waiting until you upload new or replace
                                    your artwork.
                                </p>
                            </If>
                        </div>
                    </If>
                </div>
                <ModalFooter>
                    <IfElse condition={isSubmitting}>
                        <div className="d-flex align-items-center justify-content-end">
                            <button className="btn btn-disabled bg-structure-dark color-structure-darkest">
                                <b>Submitting your proof...</b>
                            </button>
                        </div>

                        <div className="d-flex align-items-center justify-content-end gap-4">
                            <button onClick={handleClose} className="btn btn--secondary">
                                <b>Cancel</b>
                            </button>
                            <IfElse condition={canRejectProof}>
                                <button onClick={handleTriggerReject} className="btn btn-danger">
                                    <b>Reject All Artwork</b>
                                </button>
                                <button
                                    className="btn btn-disabled bg-structure-dark color-structure-darkest"
                                    disabled>
                                    <b>Reject All Artwork</b>
                                </button>
                            </IfElse>
                        </div>
                    </IfElse>
                </ModalFooter>
            </Modal>
            <IfElse condition={!!children}>
                <>{childWithProps}</>
                <button>Approve</button>
            </IfElse>
        </>
    );
};
